import { z } from 'zod';
/**
 * The inclusive bounds of a retention window, in days. Exported because FOUR places need the
 * same two numbers and none of them may hardcode them: this file's own schema, the
 * `VisitorRetentionSetting` model's doc comment (S16-TASK-011), the settings form's number
 * input `min`/`max` attributes, and the validation copy that tells a tenant what went wrong
 * (S16-TASK-014 interpolates them into the message rather than writing "1" and "3650" into an
 * English string that would then be wrong the day a bound moves).
 *
 * `1` is the floor because it is the roadmap's own demo value ("set retention to 1 day, run the
 * purge") — a floor of 7 or 30 would make the milestone's demo sentence unreachable through the
 * UI. `3650` (ten years) is the ceiling: a bound is needed so a typo cannot store a window whose
 * cutoff date arithmetic is meaningless, and ten years is longer than any retention policy a
 * workplace-visitor product has a reason to express.
 *
 * `0` IS NOT A VALID VALUE. "Erase everything immediately" is not a retention policy, it is a
 * different feature, and allowing it would make `0` a second spelling of something the purge
 * would have to special-case.
 */
export const RETENTION_MIN_DAYS = 1;
export const RETENTION_MAX_DAYS = 3650;
/**
 * What the settings form pre-fills when a tenant turns automatic deletion ON for the first time
 * (S16-TASK-014). NOT a server-side default and NOT the value a tenant with no row has — a
 * tenant that has never chosen keeps data indefinitely (`null`), and this constant never reaches
 * the database unless a human submits the form showing it.
 */
export const RETENTION_DEFAULT_DAYS = 90;
/**
 * What a reader of the visitor app's retention setting sees (docs/008 S7 — an allow-list, not a
 * row). `id`, `tenantId` and `createdAt` are deliberately absent, exactly as in
 * `visitorNotificationSettingDTOSchema`.
 *
 * `retentionDays === null` MEANS KEEP INDEFINITELY. It is not "unset and therefore zero" and not
 * an error: a tenant that has never chosen a window has no row, and the absence of a policy is
 * itself the safe policy (delete nothing). The purge (Phase 4) must read `null` as "this tenant
 * is out of range" and not as a window of zero days.
 *
 * `retentionDays` is NOT bounded here even though `updateVisitorRetentionSettingSchema` bounds
 * it. An outbound DTO is BUILT, not validated (`apps/api/src/modules/apps/visitor/dto.ts` says so
 * on `toVisitorNotificationSettingDTO`'s neighbours), and the SDK `safeParse`s responses against
 * this schema — so bounding it here would turn a row written before a bound moved into a
 * client-side throw on a screen the tenant needs in order to fix it.
 *
 * `updatedAt` is NULLABLE because a tenant that has never changed the setting has NO ROW — the
 * DTO reports the default rather than inventing a timestamp for a decision nobody made.
 */
export const visitorRetentionSettingDTOSchema = z.object({
    retentionDays: z.number().int().nullable(),
    updatedAt: z.string().datetime().nullable(),
    /**
     * The longest window this tenant's PLAN allows (`visitor.max_retention_days`), so the form can
     * say "Your plan keeps up to N days" and refuse "keep indefinitely" before submitting.
     * `null` = the plan may keep forever. OPTIONAL and additive: absent means the server did not
     * say, which a client treats as "no plan cap known", never as zero.
     *
     * When the plan's cap is shorter than `retentionDays` (a downgrade), the PURGE uses the
     * plan's cap — the stored setting is kept, so an upgrade restores it untouched.
     */
    planMaxRetentionDays: z.number().int().nullable().optional(),
});
/**
 * `PATCH /api/web/visitor/retention-settings`. One required field, no partial: a PATCH with an
 * empty body is a caller bug, not a no-op — the same rule
 * `updateVisitorNotificationSettingSchema` states, for the same reason.
 *
 * `null` is an EXPLICIT, VALID choice ("keep indefinitely"), which is why the field is
 * `.nullable()` and not `.optional()`: `.optional()` would make "stop deleting my data" and
 * "I forgot to send the field" the same request.
 *
 * NOT `z.coerce.number()` — that reads the empty string as `0` and `'abc'` as `NaN`, so a form
 * whose number input had been cleared would silently submit a value the tenant never chose. The
 * same reasoning that kept `z.coerce.boolean()` out of the notification contract.
 */
export const updateVisitorRetentionSettingSchema = z.object({
    retentionDays: z
        .number()
        .int()
        .min(RETENTION_MIN_DAYS)
        .max(RETENTION_MAX_DAYS)
        .nullable(),
});
//# sourceMappingURL=visitor-retention-setting.js.map